import { z } from "zod";

const INVALID_EMAIL = "Please enter a valid email address";

/** Trimmed, lower-cased email that reports an empty value as missing. */
const requiredEmail = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Email is required")
  .pipe(z.email(INVALID_EMAIL));

/**
 * Login form validation schema
 * 
 * Validates:
 * - Email format
 * - Password length (8-128 characters)
 * - Required fields
 */
export const loginSchema = z.object({
  email: requiredEmail,
  
  password: z
    .string()
    .min(1, "Password is required")
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password must not exceed 128 characters"),
  
  rememberMe: z.boolean().optional(),
});

export type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Teacher signup validation schema
 */
export const teacherSignupSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must not exceed 50 characters")
    .trim(),
  
  lastName: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must not exceed 50 characters")
    .trim(),
  
  email: requiredEmail,
  
  username: z
    .string()
    .min(1, "Username is required")
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must not exceed 30 characters")
    .regex(/^[a-zA-Z0-9_-]+$/, "Username can only contain letters, numbers, underscores, and hyphens")
    .trim(),
  
  password: z
    .string()
    .min(1, "Password is required")
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password must not exceed 128 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[@$!%*?&#]/, "Password must contain at least one special character (@$!%*?&#)"),
  
  confirmPassword: z
    .string()
    .min(1, "Please confirm your password"),
  
  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\d{10}$/.test(val.trim()),
      "Please enter a valid 10-digit phone number"
    ),

  phoneOtp: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{6}$/.test(val.trim()), "Please enter the 6-digit OTP"),
  
  gender: z.enum(["Male", "Female", "Other"]).optional(),
  
  invitationCode: z
    .string()
    .min(1, "Invitation code is required")
    .trim(),
}).superRefine((data, ctx) => {
  if (data.password !== data.confirmPassword) {
    ctx.addIssue({
      code: "custom",
      message: "Passwords do not match",
      path: ["confirmPassword"],
    });
  }

  if (data.phone?.trim() && !data.phoneOtp?.trim()) {
    ctx.addIssue({
      code: "custom",
      message: "Phone OTP is required when a phone number is provided",
      path: ["phoneOtp"],
    });
  }
});

export type TeacherSignupFormData = z.infer<typeof teacherSignupSchema>;

/**
 * Teacher invitation validation schema
 */
export const invitationSchema = z.object({
  email: z.preprocess(
    (value) => {
      if (typeof value === 'string' && value.trim() === '') return undefined;
      return value;
    },
    z.string().trim().toLowerCase().pipe(z.email(INVALID_EMAIL)).optional()
  ),
  
  expiresInDays: z
    .number()
    .min(1, "Minimum expiry is 1 day")
    .max(90, "Maximum expiry is 90 days"),
  
  notes: z
    .string()
    .max(500, "Notes must not exceed 500 characters")
    .optional(),
});

export type InvitationFormData = z.infer<typeof invitationSchema>;
